import { useCallback, useEffect, useRef, useState } from "react";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import onaThinkingAnimation from "@/assets/animations/ona-thinking.json";
import { cn } from "@/components/podkit/lib/cn";

type OnaThinkingProps = {
    /**
     * Animation state:
     * - "entering": Play entrance animation once
     * - "thinking": Loop the thinking animation
     * - "leaving": Play exit animation once, then call onExitComplete
     */
    state: "entering" | "thinking" | "leaving";
    /** Callback when exit animation completes */
    onExitComplete?: () => void;
    /** Callback when enter animation completes */
    onEnterComplete?: () => void;
    /** Size of the animation (default: sm / 16px to match icon size) */
    size?: "sm" | "md" | "lg";
    /** Additional CSS classes */
    className?: string;
};

const sizeClasses = {
    sm: "size-4", // 16px - matches IconSpinner size="sm"
    md: "size-6", // 24px
    lg: "size-8", // 32px
} as const;

// Frame segments from animation metadata (at 100fps)
const SEGMENTS = {
    ENTER: [0, 110] as [number, number], // 0 to 1.1s
    THINKING: [125, 400] as [number, number], // 1.25s to 4s (2.75s loop)
    LEAVE: [420, 525] as [number, number], // 4.2s to 5.25s (1.05s)
};

/**
 * OnaThinking component displays a Lottie animation of Ona in a thinking state.
 *
 * The animation has three distinct phases:
 * 1. Enter: Ona appears (plays once)
 * 2. Thinking: Ona thinks (loops continuously)
 * 3. Leave: Ona exits (plays once)
 *
 * Designed for todo items to show progress state with personality.
 * The thinking loop continues until the todo is complete, then transitions
 * smoothly through the exit animation.
 */
// Improved color filters that match content-primary exactly
// These are only used when the feedback_and_todo_improvements flag is enabled
const IMPROVED_FILTERS = {
    // Light mode: darken to match content-primary rgb(26,26,26)
    light: "brightness(0) saturate(100%) invert(10%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(88%)",
    // Dark mode: brighten to match content-primary rgb(225,225,225)
    dark: "brightness(0) saturate(100%) invert(88%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(88%)",
};

export const OnaThinking = ({ state, onExitComplete, onEnterComplete, size = "sm", className }: OnaThinkingProps) => {
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const [currentSegment, setCurrentSegment] = useState<"enter" | "thinking" | "leave">("enter");

    // Handle state changes
    useEffect(() => {
        const lottie = lottieRef.current;
        if (!lottie) return;

        switch (state) {
            case "entering":
                setCurrentSegment("enter");
                lottie.playSegments(SEGMENTS.ENTER, true);
                break;

            case "thinking":
                // If we're already in thinking state, let it continue looping
                if (currentSegment === "thinking") return;

                setCurrentSegment("thinking");
                lottie.playSegments(SEGMENTS.THINKING, true);
                break;

            case "leaving":
                // If we just started a thinking loop, let it finish before leaving
                // This prevents jarring interruptions
                if (currentSegment === "thinking") {
                    // Wait for current thinking loop to complete
                    // The onLoopComplete handler will transition to leave
                    setCurrentSegment("leave");
                } else {
                    setCurrentSegment("leave");
                    lottie.playSegments(SEGMENTS.LEAVE, true);
                }
                break;
        }
    }, [state, currentSegment]);

    // Handle animation events
    const handleComplete = useCallback(() => {
        const lottie = lottieRef.current;
        if (!lottie) return;

        if (currentSegment === "enter") {
            // Enter complete, transition to thinking
            onEnterComplete?.();
            setCurrentSegment("thinking");
            lottie.playSegments(SEGMENTS.THINKING, true);
        } else if (currentSegment === "thinking") {
            // Thinking segment complete, check if we should leave or loop
            if (state === "leaving") {
                setCurrentSegment("leave");
                lottie.playSegments(SEGMENTS.LEAVE, true);
            } else {
                // Loop thinking again
                lottie.playSegments(SEGMENTS.THINKING, true);
            }
        } else if (currentSegment === "leave") {
            // Leave complete, notify parent
            onExitComplete?.();
        }
    }, [currentSegment, state, onExitComplete, onEnterComplete]);

    const getFilter = () => {
        // Check if dark mode is active
        const isDarkMode = document.documentElement.classList.contains("dark");
        return isDarkMode ? IMPROVED_FILTERS.dark : IMPROVED_FILTERS.light;
    };

    return (
        <div
            className={cn("relative", sizeClasses[size], className)}
            style={{
                // Theme-aware color filter
                // When improvements flag is enabled, use more accurate filters
                // Otherwise, use CSS variable (original behavior)
                filter: getFilter(),
            }}
            role="img"
            aria-label="Ona thinking"
        >
            <Lottie
                lottieRef={lottieRef}
                animationData={onaThinkingAnimation}
                loop={false}
                autoplay={false}
                onComplete={handleComplete}
                className="size-full"
                rendererSettings={{
                    preserveAspectRatio: "xMidYMid meet",
                }}
            />
        </div>
    );
};
